import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import AppLayout from '../../components/AppLayout';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { S, Icon, formatMoney } from '../regureStyles';
import { useFeature } from '../../hooks/useAccess';

const MyClientsScreen = () => {
  const { t } = useTranslation();
  const history = useHistory();
  // Commissions off means the concept is absent, not zeroed. This KPI rendered
  // "OUTSTANDING COMMISSION € 0 · Awaiting settlement" for both MOK sponsors —
  // a card advertising a module the tenant does not have, and implying a
  // settlement process that does not exist for them.
  const commissionsEnabled = useFeature('commissions');
  const userLogin = useSelector(state => state.userLogin);
  // What this tenant calls a sub-national division. Swiss tenants say
  // Canton, Spanish ones say Region; the value is already in TenantConfig.
  const stateLabel = useSelector(state => state?.tenantConfig?.config?.locale?.stateLabel) || '';
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (userLogin?.userInfo?.status !== 'success') return;

    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(`${apiUrl}/me/my-clients`, { withCredentials: true });
        setClients(response.data?.data || []);
        setFilteredClients(response.data?.data || []);
      } catch (err) {
        setError(err.response?.data?.message || t('screens.portal.failedLoadClients'));
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [userLogin, apiUrl]);

  useEffect(() => {
    if (search.trim() === '') {
      setFilteredClients(clients);
    } else {
      const query = search.toLowerCase();
      setFilteredClients(
        clients.filter(c =>
          (c.name || '').toLowerCase().includes(query) ||
          (c.company || '').toLowerCase().includes(query) ||
          (c.email || '').toLowerCase().includes(query)
        )
      );
    }
  }, [search, clients]);

  if (loading) return (
    <AppLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularLoader />
      </div>
    </AppLayout>
  );

  // Calculate stats
  const activeClientsCount = clients.length;
  const totalPolicies = clients.reduce((sum, c) => sum + (c.activePolicies || 0), 0);
  const totalPremium = clients.reduce((sum, c) => sum + (c.totalPremium || 0), 0);
  // Not computed at all when the module is off — no hidden calculation behind a
  // hidden card.
  const outstandingCommission = commissionsEnabled
    ? clients.reduce((sum, c) => sum + (c.outstandingCommission || 0), 0)
    : null;

  return (
    <AppLayout>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: `linear-gradient(135deg, ${S.bg} 0%, #f8fafc 100%)`,
        padding: '32px 40px',
        minHeight: '100%'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <span style={{ color: S.burgundy, fontWeight: 700 }}>{t('screens.portal.breadcrumbPortal')}</span> / {t('screens.portal.breadcrumbMyClients')}
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, letterSpacing: '-0.5px' }}>
              {t('screens.portal.yourClients')}
            </div>
            <div style={{ fontSize: 14, color: S.textMuted, marginTop: 6 }}>
              {t('screens.portal.yourClientsSubtitle')}
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${commissionsEnabled ? 4 : 3}, 1fr)`, gap: 20, marginBottom: 32 }}>
          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.activeClientsLabel')}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: S.textPrimary }}>
              {activeClientsCount}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.activeClientsSub')}
            </div>
          </div>

          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.totalPolicies')}
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: S.textPrimary }}>
              {totalPolicies}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.totalPoliciesSub')}
            </div>
          </div>

          <div style={{
            background: '#fff',
            border: `1px solid ${S.borderLight}`,
            borderRadius: 14,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
              {t('screens.portal.totalPremium')}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary }}>
              {formatMoney(totalPremium)}
            </div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
              {t('screens.portal.totalPremiumSub')}
            </div>
          </div>

          {commissionsEnabled && (
            <div style={{
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 14,
              padding: 24,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
                {t('screens.portal.outstandingCommission')}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: S.burgundy }}>
                {formatMoney(outstandingCommission)}
              </div>
              <div style={{ fontSize: 13, color: S.textMuted, marginTop: 8 }}>
                {t('screens.portal.outstandingCommissionSub')}
              </div>
            </div>
          )}
        </div>

        {/* Table Card */}
        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(220, 38, 38, 0.15)'
          }}>
            {error}
          </div>
        )}

        <div style={{
          background: '#fff',
          border: `1px solid ${S.borderLight}`,
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '18px 24px',
            borderBottom: `1px solid ${S.borderLight}`,
            background: S.bg
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>
              {t('screens.portal.clientPortfolio')}
            </div>
            <input
              type="text"
              placeholder={t('screens.portal.searchClients')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '8px 14px',
                border: `1px solid ${S.borderLight}`,
                borderRadius: 8,
                fontSize: 13,
                width: 240,
                outline: 'none',
                fontWeight: 500,
                transition: 'border-color 0.2s'
              }}
              onFocus={e => e.target.style.borderColor = S.burgundy}
              onBlur={e => e.target.style.borderColor = S.borderLight}
            />
          </div>

          {clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 40px', color: S.textMuted }}>
              <Icon name="users" color={S.borderLight} size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <div style={{ fontSize: 16, fontWeight: 600 }}>{t('screens.portal.noClients')}</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    t('screens.portal.clientsColClient'),
                    t('screens.portal.clientsColCompany'),
                    // The tenant already declares what it calls this:
                    // locale.stateLabel is "Canton" on a Swiss tenant and
                    // "Region" on a Spanish one. The hardcoded key told a
                    // Spanish travel-assistance tenant its travellers had a
                    // Canton. Fall back to the shipped label when a tenant
                    // has configured nothing.
                    stateLabel || t('screens.portal.clientsColCanton'),
                    t('screens.portal.clientsColActivePolicies'),
                    t('screens.portal.clientsColTotalPremium'),
                    t('screens.portal.clientsColOpenClaims'),
                  ].map(h => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '14px 24px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: S.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        borderBottom: `1px solid ${S.borderLight}`,
                        background: S.bg,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client, idx) => {
                  // A client row is only a link when the API gave us a key
                  // to link with; older payloads without one stay inert
                  // rather than navigating to an unfiltered list.
                  const openPolicies = client.clientKey
                    ? () => history.push(`/portal/my-policies?client=${encodeURIComponent(client.clientKey)}`)
                    : undefined;
                  return (
                  <tr
                    key={client.clientKey || idx}
                    onClick={openPolicies}
                    onKeyDown={openPolicies ? (e => { if (e.key === 'Enter') openPolicies(); }) : undefined}
                    tabIndex={openPolicies ? 0 : undefined}
                    role={openPolicies ? 'button' : undefined}
                    title={openPolicies ? t('screens.portal.viewClientPolicies') : undefined}
                    style={{
                      borderBottom: `1px solid ${S.borderLight}`,
                      transition: 'background 0.15s ease',
                      cursor: openPolicies ? 'pointer' : 'default',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = S.bg}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.burgundy, fontWeight: 700 }}>
                      {client.name || '—'}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                      {client.company || '—'}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {client.canton || client.region || '—'}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {client.activePolicies || 0}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                      {formatMoney(client.totalPremium, client.currency)}
                    </td>
                    <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                      {client.openClaims || 0}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default MyClientsScreen;
